import { cn } from 'lib/utils'
import { CHANNEL_META, type Channel } from './ChannelBadge'

/**
 * Canonical platform-icon renderer. Single source of truth lives in
 * `CHANNEL_META` (ChannelBadge.tsx). Everywhere a surface can render an
 * `<img>`, prefer the full-color brand-badge SVG (`iconSrc`) over the
 * monochrome lucide `Icon` — the brand badges are self-contained rounded
 * tiles (real Instagram gradient, etc.), so they need no color wrapper.
 *
 * This replaces the old per-feature icon maps (PLATFORM_ICON_STYLE,
 * CHANNEL_LOGOS, CHANNEL_ICONS, CHANNEL_ICON) that drifted out of sync and
 * shipped three different Facebook marks.
 */

/**
 * Normalize any platform-ish string to a canonical `Channel` key.
 * `LivePlatform` uses `youtube_shorts`; CHANNEL_META keys on `youtube`.
 * Unknown values fall back to `instagram` so the UI never renders blank.
 */
export function normalizeChannel(input: string): Channel {
  const key = input?.toLowerCase?.() ?? ''
  if (key === 'youtube_shorts') return 'youtube'
  // LinkedIn Company Pages (`linkedin_org`) share the LinkedIn brand/icon.
  if (key === 'linkedin_org') return 'linkedin'
  if (key in CHANNEL_META) return key as Channel
  return 'instagram'
}

/** Canonical display label for any platform string. */
export function platformLabel(channel: string): string {
  return CHANNEL_META[normalizeChannel(channel)].label
}

interface PlatformIconProps {
  /** Any platform string (LivePlatform, Channel, autopilot channel id…). */
  channel: string
  /** Rendered width/height in px. Default 20. */
  size?: number
  className?: string
  /** Tooltip; defaults to the platform label. */
  title?: string
}

/**
 * Renders the brand-badge SVG for a single platform. Falls back to the
 * tinted lucide `Icon` in a neutral chip when no SVG asset exists (email).
 */
export function PlatformIcon({ channel, size = 20, className, title }: PlatformIconProps) {
  const key = normalizeChannel(channel)
  const meta = CHANNEL_META[key]
  const label = title ?? meta.label

  if (meta.iconSrc) {
    return (
      <img
        src={meta.iconSrc}
        alt={label}
        title={label}
        width={size}
        height={size}
        className={cn('shrink-0 rounded-[4px]', className)}
      />
    )
  }

  // No brand asset (e.g. email): tinted glyph in a neutral chip. Color goes
  // on the wrapper so both lucide and our custom FC icons inherit it via
  // currentColor (the custom icon components don't accept a `style` prop).
  const Icon = meta.Icon
  return (
    <span
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[4px] border border-line bg-bg-3',
        className
      )}
      style={{ width: size, height: size, color: meta.color }}
    >
      <Icon size={Math.round(size * 0.6)} />
    </span>
  )
}

interface PlatformIconStackProps {
  /** Platform strings; deduped, order preserved. */
  channels: string[]
  size?: number
  className?: string
}

/**
 * A row of brand-badge icons — the shared "disc stack" used on Live and
 * Scheduled post cards to show where a post publishes.
 */
export function PlatformIconStack({ channels, size = 20, className }: PlatformIconStackProps) {
  const unique = [...new Set(channels)]
  if (unique.length === 0) return null
  return (
    <span className={cn('flex items-center gap-1', className)}>
      {unique.map(channel => (
        <PlatformIcon key={channel} channel={channel} size={size} />
      ))}
    </span>
  )
}
